/**
 * Replaces the dashboard's empty-state apologies with something that does
 * real work: Time to hire and Recent activity currently just explain why
 * they have nothing to show, when a setup checklist could point at why --
 * and two of the three candidate setup gaps flagged in review turned out to
 * be genuinely finishable actions (connect calendar, invite a teammate), not
 * hypothetical ones.
 *
 * Each item keeps showing its state -- checked or pending -- rather than
 * disappearing the moment it's done. A returning user who completed one of
 * two items should never see a checklist that mysteriously shrank to one
 * line with no explanation. Only once EVERY item is done does the whole
 * card go away.
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import styles from './index.module.css';

export interface ChecklistItem {
  key: string;
  label: string;
  done: boolean;
  to: string;
  actionLabel: string;
}

export interface SetupChecklistProps {
  calendarConnected: boolean;
  /** null while still loading -- rendered the same as not-yet-done. */
  teammateInvited: boolean | null;
}

const Mark = ({ done }: { done: boolean }) =>
  done ? (
    <CheckCircleFilled className={styles.markDone} aria-hidden="true" />
  ) : (
    <span className={styles.markPending} aria-hidden="true" />
  );

export const SetupChecklist = memo(
  ({ calendarConnected, teammateInvited }: SetupChecklistProps) => {
    const items: ChecklistItem[] = [
      {
        key: 'calendar',
        label: 'Connect your calendar',
        done: calendarConnected,
        to: '/calendar',
        actionLabel: 'Connect',
      },
      {
        key: 'teammate',
        label: 'Invite a teammate',
        done: teammateInvited === true,
        to: '/users',
        actionLabel: 'Invite',
      },
    ];

    if (items.every((item) => item.done)) return null;

    return (
      <div className={styles.card}>
        <h2 className={styles.title}>Get set up</h2>
        <ul className={styles.list}>
          {items.map((item) => (
            <li key={item.key} className={styles.item}>
              <Mark done={item.done} />
              <span
                className={
                  item.done
                    ? `${styles.label} ${styles.labelDone}`
                    : styles.label
                }
              >
                {item.label}
              </span>
              {!item.done && (
                <Link to={item.to} className={styles.action}>
                  <Button size="small">{item.actionLabel}</Button>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  },
);

SetupChecklist.displayName = 'SetupChecklist';

export default SetupChecklist;
